export interface SavedPresetConfig {
  format?: string;
  tone?: number;
  postStyle?: string;
  hookStyle?: string;
  spacingStyle?: string;
  perspective?: string;
  humorLevel?: string;
  audience?: string;
  emojiUsage?: string;
  targetWordCount?: number;
  customInstruction?: string;
  voiceState?: string;
  audienceDescription?: string;
  blendStyle?: string;
}

export interface SavedPreset {
  id: string;
  name: string;
  config: SavedPresetConfig;
  usageCount: number;
  lastUsedAt?: string | null;
  createdAt: string;
}

export const STUDIO_USE_CASE_LABELS: Record<string, string> = {
  work:     "Work",
  school:   "School",
  creative: "Creative",
  social:   "Social Media",
  personal: "Personal",
  commerce: "Selling & Commerce",
};

export const STUDIO_SUB_PERSONA_LABELS: Record<string, string> = {
  "work-email":            "External Emails",
  "work-messaging":        "Internal Messaging",
  "work-docs":             "Docs & Reports",
  "work-sales":            "Sales Writing",
  "work-leadership":       "Leadership Comms",
  "work-manager":          "Manager to Team",
  "work-upward":           "Employee to Manager",
  "work-peer":             "Peer-to-Peer",
  "school-essays":         "Academic Essays",
  "school-career":         "Career Writing",
  "school-notes":          "Study & Notes",
  "school-presentations":  "Presentations",
  "creative-content":      "Content Creator",
  "creative-newsletter":   "Newsletter Writer",
  "creative-blogger":      "Long-form / Blog",
  "creative-fiction":      "Fiction & Stories",
  "creative-artist":       "Artist & Portfolio",
  "social-linkedin":       "Personal Brand",
  "social-lifestyle":      "Lifestyle Content",
  "social-community":      "Community & Forums",
  "social-ecommerce":      "Products & Selling",
  "personal-relationships": "Relationships",
  "personal-celebration":  "Celebrations",
  "personal-difficult":    "Difficult Convos",
  "personal-journal":      "Journaling",
  "commerce-ebay":         "eBay Listings",
  "commerce-marketplace":  "FB / Craigslist",
  "commerce-etsy":         "Etsy / Handmade",
  "commerce-amazon":       "Amazon / Online Store",
  "commerce-resale":       "Resale & Secondhand",
};

export const STUDIO_PLACEHOLDER_BY_SUB: Record<string, string> = {
  "work-email":            "Who's this email to, and what do you need them to do?",
  "work-messaging":        "What's the update? Lead with the key point.",
  "work-docs":             "What's this document about? What's the main objective?",
  "work-sales":            "Who are you writing to, and what outcome do you need?",
  "work-leadership":       "What's the message? What do you need the team to know or feel?",
  "work-manager":          "Who on your team is this for, and what do you need to communicate?",
  "work-upward":           "What are you telling your manager? What outcome do you need?",
  "work-peer":             "Who's the other team, and what do you need to say or get done?",
  "school-essays":         "What's your argument or the question you're answering?",
  "school-career":         "What role or opportunity are you writing for?",
  "school-notes":          "What topic or concept do you need to capture?",
  "school-presentations":  "What's your talk about, and who's in the room?",
  "creative-content":      "What's the hook? What's the twist or the takeaway?",
  "creative-newsletter":   "What's the idea or story for this issue?",
  "creative-blogger":      "What's your take? What do you want people to walk away thinking?",
  "creative-fiction":      "Set the scene — who's there, what happens, what's the tension?",
  "creative-artist":       "What's the work, and what do you want people to understand about it?",
  "social-linkedin":       "What insight or story do you want to share with your network?",
  "social-lifestyle":      "What moment or vibe are you capturing?",
  "social-community":      "What do you want to say to the community?",
  "social-ecommerce":      "What are you selling, and who's the perfect buyer?",
  "personal-relationships": "What do you want to say, and to whom?",
  "personal-celebration":  "Who's being celebrated, and what makes them special?",
  "personal-difficult":    "What's the situation? What do you need to say honestly but kindly?",
  "personal-journal":      "What's on your mind? No filter needed.",
  "commerce-ebay":         "What item are you listing? Condition, key details, what makes it worth buying?",
  "commerce-marketplace":  "What are you selling? Be specific — size, condition, price point.",
  "commerce-etsy":         "Describe your product — materials, size, who it's perfect for.",
  "commerce-amazon":       "What's the product? Who buys it, and what problem does it solve?",
  "commerce-resale":       "What item, brand, size, condition? Any notable details to mention?",
};

export const STUDIO_PLACEHOLDER_BY_USE_CASE: Record<string, string> = {
  work:     "What do you need to write for work? Who's it for?",
  school:   "What's the assignment or topic you're working on?",
  creative: "What do you want to create? Give me the idea.",
  social:   "What do you want to say to your audience?",
  personal: "What do you want to write? Who's it for?",
  commerce: "What are you listing or selling? Include key details.",
};

export const USE_CASE_TO_SUGGESTED_CATEGORIES: Record<string, string[]> = {
  work:     ["Career", "Work & Professional", "Customer & Support"],
  school:   ["School & Learning", "Career", "Speaking & Stage"],
  creative: ["Creative & Writing", "Content / Expression", "Social Media Content"],
  social:   ["Social Media Content", "Content / Expression", "Marketing & Ads"],
  personal: ["Personal & Life", "Personal", "Speaking & Stage"],
  commerce: ["E-commerce & Listings", "Marketplace", "Marketing & Ads"],
};
